/* eslint-disable max-len */
/* eslint-disable camelcase */
import React, { useState } from 'react';
import { Clipboard } from '@capacitor/clipboard';
import toast from 'react-hot-toast';
import ColumnRenderer from 'common/molecules/columnRenderer/ColumnRenderer';
import { createEditHandler } from 'pages/wholeme/util/profile_util';
import {
  Avatar, Box, IconButton, styled, Stack,
} from '@mui/material';
import WholeMeProfileSection from 'pages/wholeme/molecules/wholeMeProfileSection/WholeMeProfileSection';
import WholeMeProfileSectionRow from 'pages/wholeme/molecules/wholeMeProfileSectionRow/WholeMeProfileSectionRow';
import WholeMeProfileAvatar from 'pages/wholeme/molecules/wholeMeProfileAvatar/WholeMeProfileAvatar';
import PillRenderer from 'common/molecules/pillRenderer/PillRenderer';
import { containerStyles } from 'common/molecules/pillRenderer/pillRendererStyles';
import appStrings from 'common/app_strings';
import appColors from 'common/app_colors';
import { firstOptionOf, firstValueOf, resolveDefaultIcons } from '../../util/profile_util';
import { previewAvatar } from './wholeMeProfileIdentityStyles';
import { RoundEditIcon } from '../../icons/Icons';
import {
  columnContainerStyle,
  childrenContainerStyle,
} from '../wholeMeProfileAvatar/WholeMeAvatarStyles';
import {
  affinitiesFromProfile,
  filteredQuizItems,
  resolveQuizIcon,
} from './utils';
import ProfileSponsorshipRender from '../profileSponsorshipRender/ProfileSponsorshipRender';
import { openInBrowser } from '../../../../helpers/broswer';

const defaultProfileImage = '/assets/profile/default-profile-image.svg';

const { wholeMe: { wholeMeIdentity: STRINGS } } = appStrings;
const { text: { darkTaupe } } = appColors;

const copyCode = async (string) => {
  await Clipboard.write({ string });
  toast('Copied Code!', { id: `mc-${string}` });
};

const ImageContainer = styled('div')({ margin: '10px 9px 0 0' });

const titleStyles = {
  color: '#2D2D2D',
  fontSize: '18px',
  fontWeight: 700,
  lineHeight: 'normal',
};

const RenderView = ({
  profile,
  onEdit,
  setIsEditMode,
  isViewingAdmin,
  permissions,
  refreshProfile,
  userPermissions,
}) => {
  const {
    city,
    state,
    job_title,
    quiz = [],
    user_match_code,
    user_sponsors,
    linkedIn_url,
  } = profile;

  const affinities = affinitiesFromProfile(profile);
  const quizItems = filteredQuizItems(quiz);
  const isScholar = profile?.roles?.some((role) => role?.name === 'Scholar');

  const avatarProfile = {
    profile_image: profile.profile_image,
    first_name: profile.first_name,
    last_name: profile.last_name,
    role: profile.role,
    calendly_url: firstValueOf(profile.calendly_url),
    relationshipStatus: profile.relationshipStatus,
    teamCount: profile.teamCount,
    groupCount: profile.groupCount,
    donorboxId: profile.donorboxId,
    id: profile.id,
    roles: profile?.roles,
  };

  return (
    <Stack sx={childrenContainerStyle}>
      <Box sx={columnContainerStyle}>
        <WholeMeProfileAvatar
          profile={avatarProfile}
          permissions={permissions}
          userPermissions={userPermissions}
          editMeta={{ onEdit, setIsEditMode }}
          refreshProfile={refreshProfile}
        />
        <ColumnRenderer>
          {firstValueOf(city) && firstValueOf(state) && (
            <WholeMeProfileSectionRow
              styleOverrides={{ paddingBottom: '0' }}
              iconSrc="assets/icons/identity-affiliation/location-blue.svg"
              label={
                isViewingAdmin
                  ? `${firstValueOf(city)}`
                  : `${firstValueOf(city)}, ${firstValueOf(state)}`
              }
              labelStyle={{ color: darkTaupe }}
            />
          )}
          {affinities.map((next) => (
            <WholeMeProfileSectionRow
              styleOverrides={{ paddingBottom: '0' }}
              key={next.label}
              iconSrc={next.iconSrc}
              label={next.label}
              labelStyle={{ color: darkTaupe }}
            />
          ))}
          {isViewingAdmin && quizItems.map((response) => (
            <WholeMeProfileSectionRow
              styleOverrides={{ paddingBottom: '0' }}
              key={response.value}
              iconSrc={resolveQuizIcon(response.option_id, response.suboption_id, response.value)}
              label={`${response.value}`}
              labelStyle={{ color: darkTaupe }}
            />
          ))}
          {firstValueOf(job_title) && (
            <WholeMeProfileSectionRow
              styleOverrides={{ paddingBottom: '0' }}
              iconSrc="assets/profile/career/blue-briefcase.svg"
              label={`${firstValueOf(job_title)}`}
              labelStyle={{ color: darkTaupe }}
            />
          )}
          {firstValueOf(linkedIn_url) && (
            <WholeMeProfileSectionRow
              styleOverrides={{ paddingBottom: '0', cursor: 'pointer' }}
              iconSrc={resolveDefaultIcons('linkedIn')}
              onClickOverride={() => openInBrowser(firstValueOf(linkedIn_url) || '')}
              label="LinkedIn"
              labelStyle={{ color: darkTaupe, textDecoration: 'underline' }}
            />
          )}
          {firstValueOf(user_match_code) && (
            <WholeMeProfileSectionRow
              styleOverrides={{ paddingBottom: '0' }}
              iconSrc={resolveDefaultIcons('user_match_code')}
              onClickOverride={() => copyCode(firstValueOf(user_match_code))}
              label={`${firstValueOf(user_match_code)}`}
              labelStyle={{ color: darkTaupe }}
            />
          )}
          {isScholar && (
            <WholeMeProfileSectionRow
              styleOverrides={{ paddingBottom: '0' }}
              iconSrc="/assets/profile/wholeMe/scholar-verified.svg"
              label="Scholar"
              labelStyle={{ color: darkTaupe }}
            />
          )}
        </ColumnRenderer>
        {user_sponsors?.length > 0 && (
          <Stack sx={{ marginTop: '10px', paddingLeft: '4px' }}>
            <ProfileSponsorshipRender meta={user_sponsors} />
          </Stack>
        )}
      </Box>
    </Stack>
  );
};

const RenderEdit = ({ profile, onEdit, setIsEditMode }) => {
  const {
    profile_image, gender, ethnicity, city, state, job_title, birthday,
  } = profile;
  const affinities = affinitiesFromProfile(profile);
  const [jobTitle, genderVal, cityVal, stateVal, ethnicityVal, birthdayVal] = [
    firstValueOf(job_title),
    firstOptionOf(gender),
    firstValueOf(city),
    firstValueOf(state),
    firstOptionOf(ethnicity),
    firstValueOf(birthday),
  ];

  return (
    <WholeMeProfileSection
      expanded
      title={STRINGS.identity}
      titleSx={titleStyles}
      headerSx={{ marginBottom: '12px' }}
      titleIcon={(
        <ImageContainer>
          <Avatar
            sx={previewAvatar}
            src={profile_image}
            onClick={createEditHandler(onEdit, 'identity', 'photo')}
          >
            <img style={previewAvatar} src={defaultProfileImage} alt="profile" />
          </Avatar>
          <IconButton sx={{ padding: 0 }} onClick={createEditHandler(onEdit, 'identity', 'photo')}>
            <RoundEditIcon width={37} height={37} position="absolute" left={65} bottom={16} />
          </IconButton>
        </ImageContainer>
      )}
      onTitleClick={() => setIsEditMode(false)}
      expandedBorder={false}
    >
      <RenderEditSection
        text={STRINGS.zip_code}
        onClick={onEdit}
        category="identity"
        step="zip"
        isCreate={!cityVal && !stateVal}
        items={cityVal && [`${cityVal}, ${stateVal}`]}
      />
      <RenderEditSection
        text={STRINGS.gender}
        onClick={onEdit}
        category="identity"
        step="gender"
        isCreate={!genderVal}
        items={genderVal && [genderVal]}
      />
      <RenderEditSection
        text={STRINGS.affinity}
        onClick={onEdit}
        category="identity"
        step="affinity"
        isCreate={affinities.length < 1}
        items={affinities.map((a) => a.label)}
      />
      <RenderEditSection
        text={STRINGS.ethnicity}
        onClick={onEdit}
        category="identity"
        step="ethnicity"
        isCreate={!ethnicityVal}
        items={ethnicityVal && [ethnicityVal]}
      />
      <RenderEditSection
        text={STRINGS.occupation}
        onClick={onEdit}
        category="career"
        step="occupation"
        isCreate={!jobTitle}
        items={jobTitle && [jobTitle]}
      />
      <RenderEditSection
        text={STRINGS.birth_date}
        onClick={onEdit}
        category="identity"
        step="birthday"
        isCreate={!birthdayVal}
        items={birthdayVal && [birthdayVal]}
      />
    </WholeMeProfileSection>
  );
};

const RenderEditSection = ({
  onClick, text, category, step, isCreate, items,
}) => (
  <div style={{ marginBottom: '12px' }}>
    <WholeMeProfileSectionRow
      styleOverrides={{ paddingBottom: '0' }}
      label={text}
      onEdit={onClick}
      navigationMeta={{ categoryId: category, stepId: step }}
      isCreate={isCreate}
      labelStyle={{ fontWeight: 700, lineHeight: 'normal', color: '#063D8F' }}
    />
    <PillRenderer
      items={items || []}
      containerStyleOverrides={{ ...containerStyles, margin: '8px 0 0 22px' }}
    />
  </div>
);

const WholeMeProfileIdentity = ({
  profile,
  onEdit,
  isViewingAdmin,
  permissions,
  refreshProfile,
  userPermissions,
}) => {
  const [isEditMode, setIsEditMode] = useState(false);

  if (!profile) {
    return null;
  }

  return (
    <div>
      {isEditMode && <RenderEdit profile={profile} onEdit={onEdit} setIsEditMode={setIsEditMode} />}
      {!isEditMode && (
        <RenderView
          profile={profile}
          onEdit={onEdit}
          setIsEditMode={setIsEditMode}
          isViewingAdmin={isViewingAdmin}
          permissions={permissions}
          userPermissions={userPermissions}
          refreshProfile={refreshProfile}
        />
      )}
    </div>
  );
};

export default WholeMeProfileIdentity;
